<template>
  <div class="upload-list-item pa-3 caption d-flex flex-nowrap">
    <div class="flex-grow-1">
      <div
        class="upload-list-item__title text-body-2 font-weight-bold mb-2"
      >
        {{ referenceFile.name }}
      </div>
      <upload-list-item-field
        v-if="referenceFile.inProgress()"
        class="mb-2"
        label="Duration"
      >
        {{ duration }}
      </upload-list-item-field>
      <upload-list-item-field v-else class="mb-2" label="Completed">
        {{ completedAt }}
      </upload-list-item-field>
      <upload-list-item-field-source
        class="mb-2"
        :source="referenceFile.source"
      />
      <upload-list-item-field-outcome
        v-if="!!referenceFile.preScreenedOutcome"
        class="mb-2"
        :outcome="referenceFile.preScreenedOutcome"
      />
      <upload-list-item-field class="mb-2" label="Upload Breakdown">
        <v-tooltip v-if="showDuplicateWarning" bottom max-width="300">
          <template #activator="{ on }">
            <v-icon small color="warning" class="ml-2" v-on="on">
              warning
            </v-icon>
          </template>
          <span>
            Duplicate detection may need to be re-run if it failed
            during upload processing. Use the "Re-run Duplicates"
            button to retry.
          </span>
        </v-tooltip>
      </upload-list-item-field>
      <upload-breakdown
        :reference-file="referenceFile"
        :uploads-in-progress="hasUploadsInProgress"
      />
    </div>
    <div
      class="upload-list-item__actions d-flex flex-column justify-space-between align-end"
    >
      <upload-status-chip
        v-if="referenceFile.isDone()"
        :reference-files="[referenceFile]"
      />
      <processing-progress-bar
        v-else
        :reference-file="referenceFile"
      />
      <div class="d-flex">
        <template v-for="(action, i) in actions">
          <v-tooltip
            v-if="
              action.event === 're-run-duplicates' &&
              reRunButtonDisabled &&
              reRunDisabledReason
            "
            :key="`tooltip-${i}`"
            bottom
            max-width="320"
          >
            <template #activator="{ on }">
              <span
                :class="{ 'mr-2': i !== actions.length - 1 }"
                v-on="on"
              >
                <rounded-action-button
                  :icon="action.icon"
                  :disabled="true"
                  :loading="processingDuplicates"
                  @click="$emit(action.event)"
                >
                  {{ action.label }}
                </rounded-action-button>
              </span>
            </template>
            <span>{{ reRunDisabledReason }}</span>
          </v-tooltip>

          <rounded-action-button
            v-else
            :key="`action-${i}`"
            :icon="action.icon"
            :class="{ 'mr-2': i !== actions.length - 1 }"
            :disabled="
              action.event === 're-run-duplicates' &&
              reRunButtonDisabled
            "
            :loading="
              action.event === 're-run-duplicates' &&
              processingDuplicates
            "
            @click="$emit(action.event)"
          >
            {{ action.label }}
          </rounded-action-button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import RoundedActionButton from '@/components/Common/RoundedActionButton';
import UploadListItemField from '@/components/UploadsPage/UploadListItemField';
import UploadListItemFieldSource from '@/components/UploadsPage/UploadListItemFieldSource';
import UploadListItemFieldOutcome from '@/components/UploadsPage/UploadListItemFieldOutcome';
import UploadBreakdown from '@/components/UploadsPage/UploadBreakdown';
import UploadStatusChip from '@/components/UploadsPage/UploadStatusChip';
import ProcessingProgressBar from '@/components/UploadReferences/ProcessingProgressBar';
import { ReferenceFile } from '@/models';
import { format } from 'date-fns';

const ACTIONS = [
  {
    icon: 'download',
    label: 'Download',
    event: 'download',
  },
  {
    icon: 'broken_image',
    label: 'Download Invalid',
    event: 'download-invalid',
    enabled: (referenceFile) => !!referenceFile.invalidUri,
  },
  {
    icon: 'find_replace',
    label: 'Re-run Duplicates',
    event: 're-run-duplicates',
    enabled: (referenceFile) => referenceFile.isDone(),
  },
];

export default {
  name: 'UploadListItem',
  components: {
    RoundedActionButton,
    UploadListItemField,
    UploadListItemFieldSource,
    UploadListItemFieldOutcome,
    UploadBreakdown,
    UploadStatusChip,
    ProcessingProgressBar,
  },
  props: {
    referenceFile: {
      type: ReferenceFile,
      required: true,
    },
    downloading: {
      type: Boolean,
      default: false,
    },
    processingDuplicates: {
      type: Boolean,
      default: false,
    },
    hasUploadsInProgress: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['download', 'download-invalid', 're-run-duplicates'],
  data: () => ({ duration: null, durationInterval: null }),
  computed: {
    completedAt() {
      if (this.referenceFile.inProgress()) return null;
      return format(
        this.referenceFile.updatedAt,
        'dd/MM/yyyy hh:mm a'
      );
    },
    actions() {
      return ACTIONS.filter((action) =>
        action.enabled ? action.enabled(this.referenceFile) : true
      );
    },
    showDuplicateWarning() {
      return this.referenceFile.failed();
    },
    reRunButtonDisabled() {
      return this.processingDuplicates || this.hasUploadsInProgress;
    },
    reRunDisabledReason() {
      if (this.processingDuplicates) {
        return 'Duplicate detection is currently running for this review.';
      }
      if (this.hasUploadsInProgress) {
        return 'Duplicate detection can be re-run after all uploads in this review have finished processing.';
      }
      return '';
    },
  },
  mounted() {
    if (this.referenceFile.inProgress()) {
      this.updateDuration();
      this.durationInterval = setInterval(this.updateDuration, 500);
    }
  },
  beforeDestroy() {
    clearInterval(this.durationInterval);
  },
  methods: {
    updateDuration() {
      this.duration = this.referenceFile.durationString();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';

.upload-list-item {
  border-radius: 12px;
  background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
  border: 1px solid map-get($grey, 'lighten-3');
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.3s ease, transform 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      map-get($blue, 'lighten-1') 0%,
      map-get($teal, 'base') 50%,
      map-get($orange, 'darken-1') 100%
    );
    opacity: 0.8;
  }

  // Visual separator between the content column and the action-button column,
  // plus spacing between the stacked status chip and buttons within it.
  &__actions {
    gap: 12px;
    border-left: 1px solid map-get($grey, 'lighten-3');
    padding-left: 16px;
    margin-left: 16px;
  }

  &__title {
    letter-spacing: 0.01em;
  }
}
</style>
